import { useEffect, useMemo, useRef, useState } from "react";

import type { AircraftSectionExtractionBinding } from "./aircraftSectionBindingApi";
import {
  requestAircraftSectionExtraction,
  type AircraftSectionExtractionResult,
  type AircraftSectionLoop,
} from "./aircraftSectionExtractionApi";
import {
  buildAircraftSectionExtractionCommand,
  initialAircraftSectionDraft,
  SECTION_PROJECTIONS,
  SECTION_SOLVER_CONTRACTS,
  type AircraftSectionDraft,
  type AircraftSectionProjection,
  type AircraftSectionSolver,
} from "./aircraftSectionDesignModel";
import "./aircraft-section-design.css";

export type AircraftSectionBindingPhase = "pending" | "ready" | "unavailable" | "error";

interface AircraftSectionDesignLabProps {
  readonly accepted3dSignature: string;
  readonly binding: AircraftSectionExtractionBinding | null;
  readonly bindingMessage: string | null;
  readonly bindingPhase: AircraftSectionBindingPhase;
  readonly taskDriven?: boolean;
}

type SectionPhase = "idle" | "pending" | "authoritative" | "error";

const shortRevision = (value: string) => {
  const digest = value.replace(/^sha256:/, "").replace(/^.*-/, "");
  return `${digest.slice(0, 7)}…${digest.slice(-5)}`;
};

function loopPaths(loops: readonly AircraftSectionLoop[]) {
  const points = loops.flatMap((loop) => loop.pointsUvM);
  if (!points.length) return new Map<string, string>();
  const u = points.map((point) => point[0]);
  const v = points.map((point) => point[1]);
  const [minU, maxU, minV, maxV] = [Math.min(...u), Math.max(...u), Math.min(...v), Math.max(...v)];
  const spanU = Math.max(maxU - minU, 1e-9);
  const spanV = Math.max(maxV - minV, 1e-9);
  const scale = Math.min(500 / spanU, 160 / spanV);
  const offsetX = 30 + (500 - spanU * scale) / 2;
  const offsetY = 30 + (160 - spanV * scale) / 2;
  return new Map(loops.map((loop) => {
    const commands = loop.pointsUvM.map((point, index) => {
      const x = offsetX + (point[0] - minU) * scale;
      const y = offsetY + (maxV - point[1]) * scale;
      return `${index ? "L" : "M"}${x.toFixed(3)} ${y.toFixed(3)}`;
    });
    if (loop.topology === "closed") commands.push("Z");
    return [loop.loopId, commands.join(" ")];
  }));
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  return "aircraft-section-extraction-failed";
}

export function AircraftSectionDesignLab({
  accepted3dSignature, binding, bindingMessage, bindingPhase, taskDriven = false,
}: AircraftSectionDesignLabProps) {
  const [draft, setDraft] = useState<AircraftSectionDraft>(() =>
    initialAircraftSectionDraft(binding));
  const [phase, setPhase] = useState<SectionPhase>("idle");
  const [result, setResult] = useState<AircraftSectionExtractionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [obsoleteDiscarded, setObsoleteDiscarded] = useState(0);
  const requestEpoch = useRef(0);
  const controllers = useRef(new Set<AbortController>());

  useEffect(() => {
    requestEpoch.current += 1;
    for (const controller of controllers.current) controller.abort();
    controllers.current.clear();
    setDraft(initialAircraftSectionDraft(binding));
    setPhase("idle");
    setResult(null);
    setError(null);
    setObsoleteDiscarded(0);
  }, [binding]);
  useEffect(() => () => controllers.current.forEach((controller) => controller.abort()), []);

  const paths = useMemo(() => loopPaths(result?.content.section_artifact.loops ?? []), [result]);
  const update = (next: Partial<AircraftSectionDraft>, invalidateExtraction = true) => {
    if (invalidateExtraction) {
      requestEpoch.current += 1;
      const activeRequests = controllers.current.size;
      for (const controller of controllers.current) controller.abort();
      controllers.current.clear();
      if (activeRequests) setObsoleteDiscarded((value) => value + activeRequests);
    }
    setDraft((value) => ({ ...value, ...next }));
    if (invalidateExtraction) {
      setPhase("idle");
      setError(null);
    }
  };

  const extract = async () => {
    if (!binding || bindingPhase !== "ready") return;
    const token = ++requestEpoch.current;
    const controller = new AbortController();
    controllers.current.add(controller);
    setPhase("pending");
    setError(null);
    try {
      const requestId = crypto.randomUUID();
      const command = buildAircraftSectionExtractionCommand(binding, draft, requestId);
      const next = await requestAircraftSectionExtraction(command, controller.signal);
      if (token !== requestEpoch.current) {
        setObsoleteDiscarded((value) => value + 1);
        return;
      }
      setResult(next);
      setPhase("authoritative");
    } catch (caught: unknown) {
      if (token !== requestEpoch.current || controller.signal.aborted) {
        if (!controller.signal.aborted) setObsoleteDiscarded((value) => value + 1);
        return;
      }
      setError(errorMessage(caught));
      setPhase("error");
    } finally {
      controllers.current.delete(controller);
    }
  };

  const artifact = result?.content.section_artifact ?? null;
  const receipt = SECTION_SOLVER_CONTRACTS[draft.solver];
  const canExtract = bindingPhase === "ready" && binding !== null && phase !== "pending";

  return (
    <section
      className="aa-section-lab"
      data-definition-revision={binding?.definitionPin.contentRevision ?? "unbound"}
      data-reference-revision={binding?.poseStatePin.contentRevision ?? "unbound"}
      data-task-driven={String(taskDriven)}
      data-testid="aircraft-section-design-lab"
    >
      {!taskDriven ? <header>
        <div><span>{taskDriven ? "Section" : "2D Design"}</span>
          <h2>{taskDriven ? binding?.label ?? "Create a section" : "Server section from the pinned aircraft occurrence"}</h2></div>
        <div className="aa-section-pins">
          {binding ? <><code>{shortRevision(binding.definitionPin.contentRevision)}</code>
            <i>→</i><code>{shortRevision(binding.poseStatePin.contentRevision)}</code></> : <code>V3 binding unavailable</code>}
        </div>
      </header> : null}
      <div className="aa-section-layout">
        <div className="aa-section-view">
          <div className="aa-section-view-head"><span>{binding?.label ?? (taskDriven ? "Section" : "No verified section source")}</span><code>s {draft.stationM.toFixed(2)} m</code></div>
          <svg aria-label="Authoritative extracted 2D section" viewBox="0 0 560 220">
            {artifact?.loops.map((loop) => <path
              className="aa-section-loop"
              d={paths.get(loop.loopId)}
              data-body-id={loop.segmentOwnership[0]?.bodyId}
              data-evidence="authoritative-server-section"
              data-face-id={loop.segmentOwnership[0]?.faceId}
              data-loop-id={loop.loopId}
              data-loop-order={loop.order}
              data-testid="aircraft-section-loop"
              key={loop.loopId}
            />)}
            {artifact ? <text x="28" y="204">{artifact.loops.length} owned loop(s)</text>
              : <text x="28" y="112">No section yet</text>}
          </svg>
          {!taskDriven ? <div className="aa-section-accepted" data-accepted-3d-signature={accepted3dSignature} data-testid="aircraft-section-accepted-3d">
            <i />3D view stays visible while the section is created
          </div> : null}
        </div>
        <div className="aa-section-controls">
          {!taskDriven ? <div className="aa-section-source-grid">
            <label>Occurrence<input disabled value={binding?.occurrence.occurrenceId ?? "unbound"} /></label>
            <label>Source<input disabled value={binding ? "Pinned physical pose" : "No V3 source"} /></label>
          </div> : null}
          <label className="aa-section-range"><span>{taskDriven ? "Section position" : "Plane normal coordinate"} <output>{draft.stationM.toFixed(2)} m</output></span><input aria-label={taskDriven ? "Section position" : "Section plane"} aria-valuetext={`${taskDriven ? "Section position" : "Section plane"} ${draft.stationM.toFixed(2)} metres`} data-testid="aircraft-section-plane" max="1000" min="-1000" onChange={(event) => update({ stationM: Number(event.target.value) })} step="0.01" type="range" value={draft.stationM} /></label>
          {!taskDriven ? <label>Analysis projection<select data-testid="aircraft-section-projection" onChange={(event) => update({ projection: event.target.value as AircraftSectionProjection }, false)} value={draft.projection}>{SECTION_PROJECTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label> : null}
          {!taskDriven ? <><label>Solver setup<select data-testid="aircraft-section-solver" onChange={(event) => update({ solver: event.target.value as AircraftSectionSolver }, false)} value={draft.solver}><option value="flexfoil">FlexFoil</option><option value="2d-rans">2D RANS</option></select></label>
            <label className="aa-section-range"><span>Planned cove clearance <output>{draft.clearanceMm.toFixed(2)} mm</output></span><input aria-label="Control clearance" data-testid="aircraft-section-edit-clearance" disabled max="3" min="0.1" step="0.05" type="range" value={draft.clearanceMm} /></label></> : null}
          <div className="aa-section-binding" data-phase={bindingPhase} data-testid="aircraft-section-binding-status" role="status">
            {bindingPhase === "pending" ? "Checking the selected geometry…"
              : bindingPhase === "ready" ? "Source ready"
              : bindingMessage ?? "No section source is available for this selection."}
          </div>
          {artifact ? <details className="aa-section-evidence" data-technical-details open={!taskDriven}>
            <summary>{taskDriven ? "Technical details" : "Authoritative extraction evidence"}</summary>
            <div data-testid="aircraft-section-operation-run"><span>OperationRun</span><code>{result?.operation_id}</code></div>
            <div><span>ArtifactVersion</span><code>{result?.artifact_version_id}</code></div>
            <div data-revision={artifact.artifactRevision} data-testid="aircraft-section-artifact-revision"><span>Section revision</span><code>{artifact.artifactRevision}</code></div>
            <div data-testid="aircraft-section-qc"><span>QC</span><code>{artifact.qc.status} · ownership {artifact.qc.ownershipCoverage} · {artifact.qc.loopCount} loop(s)</code></div>
            <div data-fidelity={artifact.fidelity.status} data-testid="aircraft-section-fidelity"><span>Fidelity</span><code>{artifact.fidelity.status} · Δ ≤ {artifact.fidelity.maximumDeviationM.toExponential(2)} m</code></div>
            {artifact.loops.map((loop) => <div data-loop-order={loop.order} data-testid="aircraft-section-ownership" key={loop.loopId}>
              <span>Loop {loop.order}</span><code>{loop.segmentOwnership[0]?.effectiveOwnershipId} · body {loop.segmentOwnership[0]?.bodyId} · faces {[...new Set(loop.segmentOwnership.map((row) => row.faceId))].join(", ")}</code>
            </div>)}
          </details> : null}
          {!taskDriven ? <div data-testid="aircraft-section-solver-receipt" hidden><span>Prepared solver contract</span><code>{receipt.label} · {shortRevision(receipt.revision)}</code></div> : null}
          {!taskDriven ? <><section className="aa-section-honesty" data-testid="aircraft-section-design-boundary">
            <strong>Authority boundary</strong>
            <span>Section extraction is a server OperationRun over an exact stored source pin. Solver OperationRun is not available. Apply through a reviewed Thread transaction is not available.</span>
          </section>
          <section className="aa-section-next" data-testid="aircraft-section-design-next-slice">
            <strong>Still to connect</strong>
            <ol>
              <li data-step="edit">Editable, independently versioned SectionDesign.</li>
              <li data-step="solve">Explicit analysis projection plus a real FlexFoil or 2D RANS adapter.</li>
              <li data-step="apply">Preview, re-slice verification, and atomic Apply to the aircraft and Thread version.</li>
            </ol>
          </section></> : null}
          {obsoleteDiscarded ? <p className="aa-section-stale" data-testid="aircraft-section-stale">Obsolete request discarded · {obsoleteDiscarded}</p> : null}
          {error ? <p className="aa-section-error" data-testid="aircraft-section-error" role="alert">{error}</p> : null}
          <div className="aa-section-actions">
            {!taskDriven || bindingPhase === "ready" ? <button data-testid="aircraft-section-extract" disabled={!canExtract} onClick={() => void extract()} type="button">{phase === "pending" ? "Creating…" : artifact ? "Update section" : "Create section"}</button> : null}
            {!taskDriven ? <><button data-testid="aircraft-section-run-flexfoil" disabled type="button">Run FlexFoil</button>
              <button data-testid="aircraft-section-run-2d-rans" disabled type="button">Run 2D RANS</button>
              <button data-testid="aircraft-section-apply" disabled type="button">Apply</button></> : null}
          </div>
          <div aria-live="polite" className="aa-section-phase" data-phase={phase} data-testid="aircraft-section-status" role="status">{phase === "pending" ? "Creating section…" : phase === "authoritative" ? "Section ready" : phase === "error" ? "Section could not be created" : "No section yet"}</div>
        </div>
      </div>
    </section>
  );
}
